// unmanaged
//
// BioInterpreter tab — one self-contained panel per variant (default, or a
// named variant like "20260902"): the response viewer, then the evaluation
// log (status + chat — what used to be a separate "Evaluation" tab), then
// the rating + comment grading form. Submissions post an evaluation_entry
// with kind="bio_interpreter" so they appear in the log above with a
// distinct prefix and their star rating. Each submission APPENDS a fresh
// entry (audit trail); prior submissions are not overwritten.
//
// `variant` (None/undefined = the original/default BioInterpreter response)
// selects an entirely independent envelope/log — see evaluationStore.js and
// evaluation_service.KNOWN_VARIANTS. Two variants for the same report never
// share entries, status, or aggregates.
import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Rating,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import ReportTableBioInterpreter from "./ReportTableBioInterpreter";
import EvaluationChatPanel from "./EvaluationChatPanel";
import { useEvaluationStore } from "./store/evaluationStore";

function RatingLabel({ value }) {
  if (value === 1) return <Chip label="FAIL" color="error" size="small" />;
  if (value === 5) return <Chip label="PASS" color="success" size="small" />;
  if (value == null || value === 0) return <Chip label="Not rated" size="small" />;
  return <Chip label={`${value} / 5`} size="small" />;
}

// The 5-item grading rubric. `key` matches the postBioInterpreter param name;
// "rating" (Overall Ranking) is the pre-existing field and keeps the
// FAIL/PASS convention + feeds the envelope's average_rating aggregate.
// The other 4 are new criteria — same 1-5 scale, no FAIL/PASS framing.
const RUBRIC_ITEMS = [
  { key: "rating", label: "Overall Ranking", hint: "1 = FAIL · 5 = PASS" },
  { key: "dataRecalledAccurately", label: "Data Recalled Accurately" },
  { key: "summaryAndConclusions", label: "Summary and Conclusions" },
  { key: "scientificDiscussionAndInterpretation", label: "Scientific Discussion and Interpretation" },
  { key: "citations", label: "Citations" },
];

const EMPTY_SCORES = RUBRIC_ITEMS.reduce((acc, { key }) => ({ ...acc, [key]: 0 }), {});

export default function BioInterpreterEvaluationPanel({
  record,
  userId,
  variant,
  mdFile = "biointerpreter_response.md",
}) {
  const reportId = record?.id;
  const runOwnerId = record?.user_id || userId;

  const postBioInterpreter = useEvaluationStore((s) => s.postBioInterpreter);
  const fetchForReport = useEvaluationStore((s) => s.fetchForReport);

  const [scores, setScores] = useState(EMPTY_SCORES);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveOk, setSaveOk] = useState(false);

  const setScore = (key, val) => {
    setScores((prev) => ({ ...prev, [key]: val || 0 }));
    setSaveOk(false);
  };

  const canSubmit = useMemo(
    () => Boolean(reportId) && RUBRIC_ITEMS.every(({ key }) => scores[key] >= 1 && scores[key] <= 5),
    [reportId, scores]
  );

  const handleSubmit = async () => {
    setSaving(true); setSaveError(null); setSaveOk(false);
    try {
      await postBioInterpreter(reportId, variant, { ...scores, comment });
      await fetchForReport(reportId, variant, true);
      setSaveOk(true);
      // Leave the form populated so the reviewer can iterate; they'll
      // see the submitted entry appear in the log above.
    } catch (e) {
      setSaveError(e?.message || "Failed to submit BioInterpreter evaluation");
    } finally {
      setSaving(false);
    }
  };

  // On unmount / report or variant change, reset the transient state.
  useEffect(() => {
    setScores(EMPTY_SCORES);
    setComment("");
    setSaveOk(false);
    setSaveError(null);
  }, [reportId, variant]);

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, color: "#334155" }}>
        BioInterpreter response
      </Typography>
      <Box sx={{ mb: 3, border: "1px solid #E2E8F0", borderRadius: 1, p: 2, bgcolor: "#F8FAFC" }}>
        <ReportTableBioInterpreter userId={runOwnerId} reportId={reportId} filename={mdFile} />
      </Box>

      <Divider sx={{ mb: 2 }} />

      <EvaluationChatPanel record={record} variant={variant} />

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" sx={{ mb: 1, color: "#334155" }}>
        Submit BioInterpreter Evaluation
      </Typography>
      <Typography variant="caption" sx={{ display: "block", mb: 1.5, color: "#64748B" }}>
        Each submission appends a new entry to the evaluation log above — a chronological record of
        every reviewer's grading.
      </Typography>

      <Stack spacing={2}>
        {RUBRIC_ITEMS.map(({ key, label, hint }) => (
          <Stack key={key} direction="row" spacing={2} alignItems="center">
            <Typography variant="body2" sx={{ minWidth: 260, color: "#475569" }}>
              {label}
            </Typography>
            <Rating
              name={`bio-interpreter-${key}`}
              value={scores[key]}
              max={5}
              onChange={(_, val) => setScore(key, val)}
            />
            <RatingLabel value={scores[key]} />
            {hint && (
              <Typography variant="caption" sx={{ color: "#94A3B8" }}>
                {hint}
              </Typography>
            )}
          </Stack>
        ))}

        <TextField
          label="Comment"
          multiline
          minRows={3}
          maxRows={12}
          value={comment}
          onChange={(e) => { setComment(e.target.value); setSaveOk(false); }}
          placeholder="Notes on the BioInterpreter response — what it got right, what it missed, follow-ups."
          fullWidth
        />

        {saveError && <Alert severity="error">{saveError}</Alert>}
        {saveOk && <Alert severity="success">Submitted. See the evaluation log above for the appended entry.</Alert>}

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={!canSubmit || saving}
            onClick={handleSubmit}
          >
            {saving ? "Submitting…" : "Submit evaluation"}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
